import { useId, type ReactNode } from 'react';
import { Modal } from './Modal';
import { Button } from './Button';
import './ConfirmDialog.css';

/**
 * ConfirmDialog component props
 */
export interface ConfirmDialogProps {
  isOpen: boolean;
  title: ReactNode;
  message: ReactNode;
  /**
   * @default 'Confirm'
   */
  confirmLabel?: string;
  /**
   * @default 'Cancel'
   */
  cancelLabel?: string;
  /**
   * Visual style of the confirm button
   * @default 'danger'
   */
  variant?: 'danger' | 'primary';
  /**
   * Whether the confirmed action is running (disables cancel, spinner on confirm)
   */
  isPending?: boolean;
  /**
   * Error from the confirmed action, shown inside the dialog
   */
  error?: string | null;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Confirmation dialog built on Modal (portal, Escape, scroll lock, focus
 * restore). Replaces the hand-built `*-confirm-overlay` dialogs.
 * Usually driven by the `useConfirm` hook.
 */
export function ConfirmDialog({
  isOpen,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'danger',
  isPending = false,
  error,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const descriptionId = useId();

  const handleClose = () => {
    if (!isPending) onCancel();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={title}
      size="sm"
      role="alertdialog"
      descriptionId={descriptionId}
      closeOnOverlayClick={!isPending}
      closeOnEscape={!isPending}
      footer={
        <>
          <Button variant="secondary" onClick={handleClose} disabled={isPending}>
            {cancelLabel}
          </Button>
          <Button variant={variant} onClick={onConfirm} isLoading={isPending}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div id={descriptionId} className="confirm-dialog-message">
        {message}
      </div>
      {error && (
        <div className="confirm-dialog-error" role="alert">
          {error}
        </div>
      )}
    </Modal>
  );
}
